/**
 * Portaled dropdown chrome for the chart toolbar.
 *
 * Lifted out of `Toolbar.tsx` so a menu component can use the same popover
 * without importing the Toolbar that renders it (which would be an import
 * cycle). `Toolbar` and `DrawingToolsMenu` both read it from here.
 */
import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Z_CHROME } from './zLayers'

export function Popover({
  children,
  anchor,
  align = 'left',
  contentClassName,
}: {
  children: React.ReactNode
  anchor: 'bottom' | 'right'
  align?: 'left' | 'right'
  /** Override the default menu chrome (bg / min-width / scroll / padding) - e.g.
   *  a self-styled picker that manages its own width and internal scroll. */
  contentClassName?: string
}) {
  // Portaled to <body> and fixed-positioned from the trigger's rect, so the
  // toolbar's horizontal-scroll container (overflow-x) can't clip the menu.
  // A hidden inline marker measures the trigger wrapper (its parent) - it stays
  // in the toolbar DOM even though the menu itself does not.
  const markerRef = useRef<HTMLSpanElement>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)
  useLayoutEffect(() => {
    const wrap = markerRef.current?.parentElement
    if (!wrap) return
    const measure = () => setRect(wrap.getBoundingClientRect())
    measure()
    // Reposition while the toolbar scrolls (capture: the scroll happens on an
    // inner container, not window) or the viewport resizes.
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [])

  const style: React.CSSProperties = { position: 'fixed', zIndex: Z_CHROME }
  if (rect) {
    if (anchor === 'bottom') {
      style.top = rect.bottom + 4
      if (align === 'right') style.right = Math.max(4, window.innerWidth - rect.right)
      else style.left = rect.left
    } else {
      style.top = rect.top
      style.left = rect.right + 1
    }
  }

  return (
    <>
      <span ref={markerRef} className="hidden" aria-hidden />
      {rect &&
        createPortal(
          <div
            data-toolbar-popover=""
            onMouseDown={(e) => e.stopPropagation()}
            style={style}
            className={
              contentClassName ??
              'max-h-80 min-w-[180px] overflow-y-auto rounded-md border border-primary/15 bg-popover p-1 shadow-lg shadow-black/30'
            }
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  )
}

export function PopoverItem({
  children,
  onClick,
}: {
  children: React.ReactNode
  onClick?: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="block w-full rounded-sm px-2.5 py-1 text-left text-xs text-[hsl(var(--chart-text-muted))] transition-colors hover:bg-accent hover:text-[hsl(var(--chart-text))]"
    >
      {children}
    </button>
  )
}
